import { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";

import { GetStudyPlanUsageCall } from "../../redux/api/studyPlan.api";

const GOAL_LABEL = {
  exam: "Exam",
  mastery: "Mastery",
  general: "General",
};

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

export default function AdminStatsStudyPlans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_plans: 0, total_users: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GetStudyPlanUsageCall({ page: page + 1, limit, search: search || undefined }).then((res) => {
      if (cancelled) return;
      if (res?.status === 200 && res.data) {
        setRows(Array.isArray(res.data.items) ? res.data.items : []);
        setTotal(res.data.pagination?.total || 0);
        setSummary({
          total_plans: res.data.summary?.total_plans || 0,
          total_users: res.data.summary?.total_users || 0,
        });
      } else {
        setRows([]);
        setTotal(0);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page, limit, search, refreshTick]);

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" color="primary" variant="outlined" label={`${summary.total_plans} plans`} />
          <Chip size="small" variant="outlined" label={`${summary.total_users} people`} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Search"
            placeholder="Name, email, or subject"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            sx={{ minWidth: 220 }}
          />
          <IconButton size="small" onClick={() => setRefreshTick((value) => value + 1)} aria-label="Refresh study plan usage">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Profile</TableCell>
            <TableCell>Plan</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last used</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No study plans yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const name = row.user?.name || "Unknown";
              const detail = [
                GOAL_LABEL[row.goal] || row.goal,
                row.exam_name,
                row.level,
                row.learn_with_ai ? "AI" : null,
                row.material_name,
              ].filter(Boolean).join(" · ");
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar src={row.user?.profile_image || undefined} sx={{ width: 34, height: 34 }}>
                        {initials(name)}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip size="small" color="primary" label={name} />
                          {row.user?.profile_verified ? (
                            <VerifiedIcon sx={{ fontSize: 16, color: "info.main" }} />
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[row.user?.email, row.user?.university].filter(Boolean).join(" · ") || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{row.subject || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {detail || row.status}
                      {` · ${row.topics_count} topics · ${row.lessons_completed}/${row.lessons_count} lessons`}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.progress || 0}%</TableCell>
                  <TableCell>{formatWhen(row.created_at)}</TableCell>
                  <TableCell>{formatWhen(row.last_activity_at || row.updated_at)}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={limit}
        onRowsPerPageChange={(event) => {
          setLimit(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Box>
  );
}
